/**
 * PlaylistTileHoverBodyV1 — Hover-card body for the Infinity Carousel
 * variation.
 *
 * Layout (top-to-bottom):
 *   1. Playlist name (avenir-medium, 13/18, weight 800, $body)
 *   2. Meta line (11/16, $body-light)
 *   3. Horizontal thumbnail strip (~3-4 visible) — echoes the hero's
 *      sliding motion as a static row
 *   4. Bullet list of preview content titles (limit 5), each fading in
 *      with a 30 ms stagger via SCSS `:nth-child` animation-delay
 *   5. "+N more" line if itemCount exceeds shown titles
 *   6. Teal "View Playlist →" CTA pill (matches legacy tile + siblings)
 */

import React from 'react'
import StackedCards from '../../../../Icon/icons/StackedCards/index'

const MAX_STRIP_THUMBNAILS = 4
const MAX_VISIBLE_TITLES = 5

interface PlaylistTileHoverBodyV1Props {
  playlistName: string
  metaLine: string
  thumbnails: string[]
  titles: string[]
  itemCount: number | null
}

function PlaylistTileHoverBodyV1 ({
  playlistName,
  metaLine,
  thumbnails,
  titles,
  itemCount,
}: PlaylistTileHoverBodyV1Props): React.ReactElement {
  const stripThumbnails = thumbnails.slice(0, MAX_STRIP_THUMBNAILS)
  const shownTitles = titles.slice(0, MAX_VISIBLE_TITLES)
  const remaining = (itemCount ?? 0) - shownTitles.length

  return (
    <div className="playlist-search-tile-v1-hover">
      <span className="playlist-search-tile-v1-hover__name">{playlistName}</span>
      {metaLine && (
        <span className="playlist-search-tile-v1-hover__meta">{metaLine}</span>
      )}

      <div className="playlist-search-tile-v1-hover__strip">
        {stripThumbnails.length > 0 ? (
          stripThumbnails.map((url, i) => (
            <div
              key={`${url}-${i}`}
              className="playlist-search-tile-v1-hover__strip-card"
            >
              <img
                src={url}
                alt=""
                loading="lazy"
                className="playlist-search-tile-v1-hover__strip-img"
              />
            </div>
          ))
        ) : (
          <div className="playlist-search-tile-v1-hover__strip-placeholder">
            <StackedCards size="32px" color="rgba(0, 0, 0, 0.25)" />
          </div>
        )}
      </div>

      {shownTitles.length > 0 && (
        <ul className="playlist-search-tile-v1-hover__titles">
          {shownTitles.map((title, i) => (
            <li
              key={`${title}-${i}`}
              className="playlist-search-tile-v1-hover__title"
            >
              {title}
            </li>
          ))}
        </ul>
      )}

      {remaining > 0 && (
        <span className="playlist-search-tile-v1-hover__more">
          + {remaining} more
        </span>
      )}

      <span className="playlist-search-tile-v1-hover__cta">
        View Playlist &rarr;
      </span>
    </div>
  )
}

export default PlaylistTileHoverBodyV1
